import { FC, MouseEvent, useRef, useState } from 'react';
import { Cell, ColumnSort, SortingColumn, flexRender } from '@tanstack/react-table';
import { GridProps, PADDING, SPACING, SortOrder, TableColumn } from './models';
import { getColumnWidth, getClassNamesFromStyles } from './DataGridUtils';
import { DEFAULT_CUSTOM_STYLES } from './DataGridConstants';
import { FEATURE_FLAG_LIGHT_THEME, Icon, css, MAGNETIC_TOKENS } from '@appd/uik';

const { COLORS } = MAGNETIC_TOKENS;

const STYLES = {
  Container: css.Styles({
    display: 'flex',
    flexDirection: 'column',
    color: COLORS.DEFAULT_TEXT,
    backgroundColor: 'inherit',
  }),
  HeaderGroup: css.Styles({
    position: 'sticky',
    top: 0,
    // TODO: COLORS.CONTROL_BORDER_STRONG is wrong in dark mode; hard-coding for now
    boxShadow: `inset 0 -2px 0 ${FEATURE_FLAG_LIGHT_THEME ? COLORS.CONTROL_BORDER_STRONG : '#c1c6cc'}`,
    zIndex: 1, // Note: z-indexes of elements that should appear on top of the table should have a higher z-index
  }),
  Header: (alignment: any = 'left') =>
    css.Styles({
      textOverflow: 'ellipsis',
      overflowWrap: 'break-word',
      whiteSpace: 'nowrap',
      lineHeight: '20px',
      fontWeight: 'bold',
      textAlign: alignment,
    }),
  HeaderHover: css.Styles({
    ':hover': {
      background: COLORS.DEFAULT_BG_HOVER,
      boxShadow: `inset 0 -0.5px 0 ${COLORS.INVERSE_BG}`,
    },
  }),
  Cell: css.Styles({
    textOverflow: 'ellipsis',
    overflowWrap: 'break-word',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    lineHeight: '20px',
    fontWeight: 'normal',
    // TODO: COLORS.CONTROL_BORDER_WEAK is wrong in dark mode; hard-coding for now
    borderBottom: `1px solid ${FEATURE_FLAG_LIGHT_THEME ? COLORS.CONTROL_BORDER_WEAK : '#656c75'}`,
    verticalAlign: 'middle',
  }),
  SortControl: css.Styles({
    display: 'flex',
    flexDirection: 'column',
    height: '30px',
    width: '30px',
    margin: 'auto',
  }),
  Icon: css.Styles({
    fill: COLORS.DEFAULT_ICON_IN,
  }),
};

export const Grid: FC<GridProps> = ({
  table,
  tableRows,
  colDefs,
  onCellClick,
  onRowClick,
  onScroll,
  isFetching,
  isFetchingNextPage,
  height,
  styles = DEFAULT_CUSTOM_STYLES,
  spacing = SPACING.COMPACT,
  paddingTop,
  paddingBottom,
  selectedRowIndex,
}) => {
  const [clickedRowIndex, setClickedRowIndex] = useState<number | undefined>(selectedRowIndex);

  // Attempt fetch data when user has scrolled within 300px of the bottom of the table
  // unrelated to h-[300]px, which is the height of the component vs height of the nested table
  const infiniteScrollCallHeight = 300;
  const inputRef = useRef<HTMLDivElement | null>(null);
  if (isFetching && !isFetchingNextPage && table.getPageCount() == 0) {
    if (inputRef.current) {
      inputRef.current.scrollTop = 0;
    }
  }

  const onTableScroll = (containerRefElement?: HTMLDivElement | null) => {
    if (containerRefElement) {
      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      if (scrollHeight - scrollTop - clientHeight < infiniteScrollCallHeight && (!isFetching || !isFetchingNextPage)) {
        onScroll();
      }
    }
  };
  const gridClassNames = getClassNamesFromStyles(styles.grid || {});

  const sortHandler = (header: any) => {
    const sortId = header?.column?.columnDef?.meta?.sortBy || header.id;
    const column = table.getColumn(sortId) as SortingColumn<TableColumn>;
    let sortOrder = (column.getIsSorted() || SortOrder.DESC) == SortOrder.ASC;
    table.setSorting([
      {
        id: sortId,
        desc: sortOrder,
      } as ColumnSort,
    ]);
  };

  // temporary hardcoding for row / header heights
  return (
    <div className={gridClassNames} style={STYLES.Container}>
      <div
        className={`inline-block min-w-full overflow-y-auto ${`h-[${height}px] bg-inherit`}`}
        ref={inputRef}
        onScroll={e => onTableScroll(e.target as HTMLDivElement)}
      >
        <table className="min-w-full text-center text-sm table-auto bg-inherit">
          <thead className="bg-inherit">
            {table.getHeaderGroups().map(headerGroup => (
              <tr
                key={headerGroup.id}
                className="bg-inherit"
                style={{
                  ...STYLES.HeaderGroup,
                }}
              >
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    style={{
                      ...STYLES.Header(header.column.columnDef.meta?.contentAlignment),
                      ...{
                        minWidth: getColumnWidth(colDefs, header.column.id),
                        maxWidth: getColumnWidth(colDefs, header.column.id),
                        padding: PADDING[spacing],
                      },
                      ...(header?.column?.columnDef?.meta?.sortable ? STYLES.HeaderHover : {}),
                    }}
                    onClick={() => {
                      header?.column?.columnDef?.meta?.sortable && sortHandler(header);
                    }}
                  >
                    <div
                      style={{
                        display: 'inline-flex',
                      }}
                    >
                      <div className="m-auto">
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </div>

                      {header?.column?.columnDef?.meta?.sortable && (
                        <div className={`${css(STYLES.SortControl)}`}>
                          <Icon.Navigation.ArrowDropUp
                            style={{
                              opacity:
                                table.getColumn(header?.column?.columnDef?.meta?.sortBy || header.id)?.getIsSorted() ===
                                SortOrder.ASC
                                  ? 1
                                  : 0.5,
                              marginBottom: '-5px',
                            }}
                          />
                          <Icon.Navigation.ArrowDropDown
                            style={{
                              opacity:
                                table.getColumn(header?.column?.columnDef?.meta?.sortBy || header.id)?.getIsSorted() ===
                                SortOrder.DESC
                                  ? 1
                                  : 0.5,
                              marginTop: '-5px',
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="overflow-auto bg-inherit">
            {paddingTop != null && paddingTop > 0 && (
              <tr>
                <td style={{ height: `${paddingTop}px` }} />
              </tr>
            )}
            {tableRows.map(row => (
              <tr
                key={row.id}
                className={`transition-colors duration-150 hover:bg-slate-300/30 ${
                  row.index === clickedRowIndex ? 'bg-blue-100' : ''
                }`}
                onClick={(e: MouseEvent<HTMLTableRowElement>) => {
                  const field = (e.target as HTMLElement).closest('td')?.dataset.field;
                  const rowData = row.original;
                  if (onRowClick && onRowClick.name != 'noop') {
                    setClickedRowIndex(row.index);
                    onRowClick({ row: rowData, field });
                  }
                }}
              >
                {row.getVisibleCells().map((cell: Cell<unknown, unknown>) => (
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                  <td
                    key={cell.id}
                    style={{
                      ...STYLES.Cell,
                      ...{
                        minWidth: getColumnWidth(colDefs, cell.column.id),
                        maxWidth: getColumnWidth(colDefs, cell.column.id),
                        textAlign: cell.getContext().column.columnDef.meta?.contentAlignment ?? 'left',
                        padding: PADDING[spacing],
                      },
                    }}
                    data-field={cell.column.id}
                    onClick={() => {
                      const field = cell.column.id;
                      const value = cell.getValue();
                      const row = cell.row.original;
                      onCellClick({ field, value, row });
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
            {paddingBottom != null && paddingBottom > 0 && (
              <tr>
                <td style={{ height: `${paddingBottom}px` }} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
